import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Button, Spinner, Paragraph } from "grommet";
import { Attachment, FormClose, FormNext } from "grommet-icons";
import io from "socket.io-client";
import moment from "moment-timezone";
import { CommunicationService } from "../../services";
import { presentToastErrorContent } from "@shared/Toast";
import { SERVER_URL, authHeader } from "../../services/api";
import { useFetchHotel, useSetCurrentUser } from "./redux/hooks";
import { renderAIHtml } from "@common/renderAIHtml";
import { useTranslation } from "react-i18next";

import "./ConversationWithAI.scss";

function SecureAIScreenshot({ filename, previewUrl }) {
  const [src, setSrc] = useState(previewUrl);

  useEffect(() => {
    if (previewUrl || !filename) return undefined;
    let objectUrl;
    let active = true;
    CommunicationService.getAIScreenshot(filename)
      .then(({ data }) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(data);
        setSrc(objectUrl);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filename, previewUrl]);

  return src ? <img className="chat-photo" src={src} alt="" /> : null;
}

export default function ConversationWithAI({ panel = false, onNavigate, testAsGuest = false }) {
  const { t, i18n } = useTranslation();
  const [currentMessage, setCurrentMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [connectedToSocket, setConnectedToSocket] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingEarlierMessages, setIsLoadingEarlierMessages] =
    useState(false);
  const [socket, setSocket] = useState(null);
  const { hotel } = useFetchHotel();
  const { currentUser, isLoxeAdmin } = useSetCurrentUser();
  const hotelTimezone = hotel?.timezone || moment.tz.guess() || "UTC";

  const sendingAsSupport = (currentUser?.isSupport === true || isLoxeAdmin) && !testAsGuest;
  const [isSending, setIsSending] = useState(false);
  const [supportSentIds, setSupportSentIds] = useState(new Set());
  const [sendingSupport, setSendingSupport] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  const messageEndRef = useRef(null);
  const screenshotInputRef = useRef(null);

  useEffect(() => {
    if (!screenshot) {
      setScreenshotPreview(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(screenshot);
    setScreenshotPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [screenshot]);

  const scrollToBottom = () => {
    if (messageEndRef.current)
      messageEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
  };

  const loadMessages = useCallback(() => {
    CommunicationService.getAIMessages(undefined, testAsGuest).then(
      async (response) => {
        if (response.data) {
          const {
            messages: nextMessages,
            hasMoreMessages: nextHasMoreMessage,
          } = response.data;
          setMessages(nextMessages);
          setHasMoreMessages(nextHasMoreMessage);
          setTimeout(scrollToBottom, 1000);
        }
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  }, [testAsGuest]);

  const loadEarlierMessages = async () => {
    setIsLoadingEarlierMessages(true);
    const { data } = await CommunicationService.getAIMessages(messages[0]._id, testAsGuest);
    const { messages: newMessages, hasMoreMessages: nextHasMoreMessage } = data;
    setMessages((prevMessages) => {
      return [...newMessages, ...prevMessages];
    });
    setHasMoreMessages(nextHasMoreMessage);
    setIsLoadingEarlierMessages(false);
  };

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (hotel?._id === undefined) return;
    const newSocket = io(SERVER_URL, {
      auth: (done) => done({ protocol: 2, headers: authHeader() }), transports: ["websocket"],
    });
    let hasConnected = false;

    newSocket.on("connect_error", (error) => {
      console.error("Socket error:", error);
    });

    newSocket.on("connect", () => {
      console.log("Connected to server");
      newSocket.emit("joinHotel", { hotelId: hotel._id }, ({ ok } = {}) => {
        setConnectedToSocket(Boolean(ok));
        if (!ok) return;
        if (hasConnected) loadMessages();
        hasConnected = true;
      });
    });

    newSocket.on("disconnect", () => {
      setConnectedToSocket(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [hotel._id, loadMessages]);

  useEffect(() => {
    if (socket) {
      const receiveMessage = (message) => {
        if (Boolean(message.isTestGuest) !== testAsGuest) return;
        if (!message.fromHotel || message.fromAI) {
          setTimeout(
            () =>
              setMessages((prevMessages) =>
                prevMessages.some(
                  (existingMessage) =>
                    String(existingMessage._id) === String(message._id),
                )
                  ? prevMessages
                  : [...prevMessages, message],
              ),
            100,
          );
          setTimeout(scrollToBottom, 700);
        }
      };
      socket.on("receiveMessage", receiveMessage);
      return () => socket.off("receiveMessage", receiveMessage);
    }
    return undefined;
  }, [socket, testAsGuest]);

  const handleSendToSupport = async (message) => {
    if (sendingSupport) return;
    setSendingSupport(message._id);
    try {
      await CommunicationService.sendSupportEmail(message.text);
      setSupportSentIds((prev) => new Set([...prev, message._id]));
    } catch (error) {
      presentToastErrorContent("Failed to send support request.");
    } finally {
      setSendingSupport(null);
    }
  };

  const handleSendMessage = async () => {
    if ((!currentMessage?.trim() && !screenshot) || isSending) return;
    const trimmed = currentMessage.trim();
    const pendingId = "pending_" + Date.now();
    const pendingMessage = {
      _id: pendingId,
      fromHotel: !sendingAsSupport,
      fromSupport: Boolean(sendingAsSupport),
      fromAI: false,
      text: trimmed,
      status: "SENDING",
      createdAt: new Date().toISOString(),
      ...(screenshotPreview && { screenshotPreview }),
      ...(testAsGuest && { isTestGuest: true }),
    };
    setMessages((prev) => [...prev, pendingMessage]);
    setCurrentMessage("");
    setIsSending(true);
    setTimeout(scrollToBottom, 100);
    try {
      const response = sendingAsSupport
        ? await CommunicationService.sendSupportReply(hotel._id, trimmed, screenshot)
        : await CommunicationService.sendAIMessage(
            trimmed,
            testAsGuest,
            screenshot,
            window.location.pathname,
            i18n?.resolvedLanguage || i18n?.language || "en",
          );
      const newMessages = sendingAsSupport ? [response.data] : response.data;
      if (newMessages) {
        setMessages((prev) => {
          const responseIds = new Set(
            newMessages.map((message) => String(message._id)),
          );
          return [
            ...prev.filter(
              (message) =>
                message._id !== pendingId &&
                !responseIds.has(String(message._id)),
            ),
            ...newMessages,
          ];
        });
        setTimeout(scrollToBottom, 500);
      }
      setScreenshot(null);
    } catch (error) {
      setMessages((prev) =>
        prev.map((m) => (m._id === pendingId ? { ...m, status: "ERROR" } : m)),
      );
      presentToastErrorContent(error.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleMessageLinkClick = (event) => {
    const anchor = event.target.closest("a");
    const path = anchor?.getAttribute("href");
    if (!onNavigate || !path || !/^\/(?!\/)/.test(path)) return;
    event.preventDefault();
    onNavigate(path);
  };

  const renderMessageHtml = (text) =>
    renderAIHtml(text).replace(
      /(<a\b[^>]*href=["']\/guests\/[^"']+["'][^>]*>)(?:Voir réservation|Open reservation)(<\/a>)/gi,
      `$1${t("aiConversation.openReservation")}$2`,
    );

  return (
    <div
      className={
        panel ? "assistant-chat assistant-chat--panel" : "assistant-chat"
      }
    >
      {!panel && (
        <Paragraph
          size="small"
          color="black"
          textAlign="center"
          fill
          margin={{ top: "xsmall", bottom: "xsmall" }}
        >
          {t("aiConversation.helpLine1")} <br />
          {t("aiConversation.helpLine2")}{" "}
          <strong>{t("aiConversation.askHuman")}</strong>
          {t("aiConversation.helpLine3")} <br /> {t("aiConversation.helpLine4")}
        </Paragraph>
      )}
      <Box direction="row" justify="between" margin={{ top: "small" }}>
        {!connectedToSocket && (
          <Button
            label={t("conversation.refresh")}
            onClick={() => loadMessages()}
            primary
            color="brand"
            alignSelf="end"
          />
        )}
      </Box>
      {sendingAsSupport && (
        <div className="chat-guest-mode-notice" role="status">
          {t("aiConversation.sendingAsSupport")}
        </div>
      )}
      <Box className="assistant-chat__body" gap="none" width="100%" margin={{ top: "small" }}>
        <div
          className="chat-wrapper"
        >
          <div className="chat-container">
            <div>
              {hasMoreMessages && (
                <Box align="center" margin={{ bottom: "small" }}>
                  <button
                    className="chat-load-more-btn"
                    onClick={
                      !isLoadingEarlierMessages
                        ? loadEarlierMessages
                        : undefined
                    }
                    disabled={isLoadingEarlierMessages}
                  >
                    {isLoadingEarlierMessages ? (
                      <Spinner size="xsmall" />
                    ) : (
                      t("conversation.loadEarlier")
                    )}
                  </button>
                </Box>
              )}
              {messages &&
                (() => {
                  let latestEscalatableAIMessageId;
                  for (
                    let index = messages.length - 1;
                    index >= 0;
                    index -= 1
                  ) {
                    const candidate = messages[index];
                    if (candidate.fromSupport) break;
                    if (!testAsGuest && !sendingAsSupport && candidate.fromAI && candidate.text?.trim()) {
                      latestEscalatableAIMessageId = candidate._id;
                      break;
                    }
                  }
                  return messages.map((message, index) => {
                    const isHotel = message.fromHotel && !message.fromAI;
                    const isAI = message.fromAI;
                    const isSystem = message.system;
                    const isSupport = Boolean(message.fromSupport);
                    const isGuest =
                      !message.fromHotel && !message.fromAI && !isSupport;

                    // Day separator logic
                    const msgDate = moment.tz(message.createdAt, hotelTimezone);
                    const prevMsg = index > 0 ? messages[index - 1] : null;
                    const prevDate = prevMsg
                      ? moment.tz(prevMsg.createdAt, hotelTimezone)
                      : null;
                    const showDaySeparator =
                      !prevDate || !msgDate.isSame(prevDate, "day");

                    // Smart time: time-only if today, otherwise day + date + time
                    const isToday = msgDate.isSame(
                      moment().tz(hotelTimezone),
                      "day",
                    );
                    const timeDisplay = isToday
                      ? msgDate.format("LT")
                      : msgDate.format("ddd, MMM D \u00b7 LT");

                    let bubbleClass = "chat-bubble ";
                    let senderClass = "chat-sender ";
                    let timeClass = "chat-time ";
                    let rowClass = "chat-message-row ";

                    if (isHotel) {
                      bubbleClass += "chat-bubble--hotel";
                      senderClass += "chat-sender--hotel";
                      timeClass += "chat-time--hotel";
                      rowClass += "chat-message-row--hotel";
                    } else if (isSupport) {
                      bubbleClass += "chat-bubble--support";
                      senderClass += "chat-sender--support";
                      timeClass += "chat-time--other";
                      rowClass += "chat-message-row--other";
                    } else if (isSystem) {
                      bubbleClass += "chat-bubble--system";
                      senderClass += "chat-sender--system";
                      timeClass += "chat-time--other";
                      rowClass += "chat-message-row--other";
                    } else if (isAI) {
                      bubbleClass += "chat-bubble--ai";
                      senderClass += "chat-sender--ai";
                      timeClass += "chat-time--other";
                      rowClass += "chat-message-row--other";
                    } else {
                      bubbleClass += "chat-bubble--guest";
                      senderClass += "chat-sender--guest";
                      timeClass += "chat-time--other";
                      rowClass += "chat-message-row--other";
                    }

                    if (message.isTestGuest) {
                      bubbleClass += " chat-bubble--test-guest";
                    }

                    if (message.status === "SENDING") {
                      bubbleClass += " chat-bubble--sending";
                    } else if (message.status === "ERROR") {
                      bubbleClass += " chat-bubble--error";
                    }

                    return (
                      <React.Fragment key={message._id}>
                        {showDaySeparator && (
                          <div className="chat-date-separator">
                            <div className="chat-date-separator__line" />
                            <span className="chat-date-separator__text">
                              {isToday
                                ? t("conversation.today")
                                : msgDate.isSame(
                                      moment()
                                        .tz(hotelTimezone)
                                        .subtract(1, "day"),
                                      "day",
                                    )
                                  ? t("conversation.yesterday")
                                  : msgDate.format("ddd, MMM D")}
                            </span>
                            <div className="chat-date-separator__line" />
                          </div>
                        )}
                        <div className={rowClass}>
                          <div className={bubbleClass}>
                            {/* Sender label */}
                            {isSystem && (
                              <div className={senderClass}>
                                🔔 {t("conversation.aiAssistant")}
                              </div>
                            )}
                            {isSupport && (
                              <div className={senderClass}>
                                {t("aiConversation.loxeSupport")}
                              </div>
                            )}
                            {isAI && !isSystem && (
                              <div className={senderClass}>
                                {t("conversation.aiAssistant")}
                              </div>
                            )}
                            {isHotel && (
                              <div className={senderClass}>
                                {message.isTestGuest
                                  ? t("aiConversation.guestTest")
                                  : message.sentBy?.username ||
                                    t("aiConversation.hotel")}
                              </div>
                            )}
                            {isGuest && (
                              <div className={senderClass}>
                                {t("conversation.guest")}
                              </div>
                            )}

                            {(message.screenshot ||
                              message.screenshotPreview) && (
                              <SecureAIScreenshot
                                filename={message.screenshot}
                                previewUrl={message.screenshotPreview}
                              />
                            )}

                            {message.text && (
                              <div
                                onClick={handleMessageLinkClick}
                                dangerouslySetInnerHTML={{
                                  __html: renderMessageHtml(message.text),
                                }}
                              />
                            )}

                            {message.clientActions?.map((action) => (
                              <button
                                key={`${action.destination}-${action.path}`}
                                className="chat-navigation-action"
                                onClick={() => onNavigate?.(action.path)}
                              >
                                {action.label || t("aiConversation.openScreen")}
                                <FormNext size="small" />
                              </button>
                            ))}

                            {message.supportReport?.status === "sent" && (
                              <div className="chat-report-receipt">
                                {t("aiConversation.bugReported")}
                              </div>
                            )}

                            <div className={timeClass}>
                              {message.status === "SENDING" ? (
                                <Spinner
                                  size="xsmall"
                                  color={isHotel ? "white" : undefined}
                                />
                              ) : message.status === "ERROR" ? (
                                <span>✕ {t("conversation.failed")}</span>
                              ) : (
                                <>
                                  {timeDisplay}
                                  {isHotel &&
                                    message.status === "READ" &&
                                    " \u2713\u2713"}
                                  {isHotel &&
                                    message.status !== "READ" &&
                                    ` \u2022 ${message.status.toLowerCase()}`}
                                </>
                              )}
                            </div>
                            {isAI &&
                              !isSystem &&
                              message._id === latestEscalatableAIMessageId &&
                              (supportSentIds.has(message._id) ? (
                                <div className="chat-support-sent">
                                  ✓ {t("aiConversation.humanAsked")}
                                </div>
                              ) : (
                                <button
                                  className="chat-support-btn"
                                  onClick={() => handleSendToSupport(message)}
                                  disabled={sendingSupport === message._id}
                                >
                                  {sendingSupport === message._id ? (
                                    <>
                                      <Spinner size="xsmall" />{" "}
                                      {t("aiConversation.askingHuman")}
                                    </>
                                  ) : (
                                    t("aiConversation.askHuman")
                                  )}
                                </button>
                              ))}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  });
                })()}
            </div>
            {isSending && !sendingAsSupport && (
              <div className="chat-bubble chat-bubble--ai chat-bubble--streaming">
                <div className="chat-sender chat-sender--ai">
                  {t("conversation.aiAssistant")}
                </div>
                <div className="chat-typing-dots">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
            <div ref={messageEndRef}></div>
          </div>
          <div className="chat-input-area">
            <div className="chat-composer">
              {screenshotPreview && (
                <div className="chat-screenshot-preview">
                  <img src={screenshotPreview} alt="" />
                  <button
                    type="button"
                    title={t("aiConversation.removeScreenshot")}
                    onClick={() => setScreenshot(null)}
                  >
                    <FormClose size="small" />
                  </button>
                </div>
              )}
              <div className="chat-composer-row">
                <input
                  ref={screenshotInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) {
                      presentToastErrorContent(
                        t("aiConversation.screenshotTooLarge"),
                      );
                      return;
                    }
                    setScreenshot(file);
                  }}
                />
                <Button
                  size="small"
                  hoverIndicator="accent-4"
                  primary
                  color="status-warning"
                  icon={<Attachment size="small" />}
                  aria-label={t("aiConversation.attachScreenshot")}
                  title={t("aiConversation.attachScreenshot")}
                  onClick={() => screenshotInputRef.current?.click()}
                  disabled={isSending}
                  className="chat-attachment-button"
                />
                <textarea
                  value={currentMessage}
                  onChange={(event) => setCurrentMessage(event.target.value)}
                  placeholder={t("conversation.placeholder")}
                  className="message-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
              </div>
            </div>
            <div
              className="chat-composer-actions"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Button
                label={
                  isSending ? t("conversation.sending") : t("conversation.send")
                }
                onClick={handleSendMessage}
                primary
                disabled={isSending}
              />
            </div>
          </div>
        </div>
      </Box>
    </div>
  );
}
